import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Music, Gamepad2 } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import RulesSidebarNav from './RulesSidebarNav';

const AppShell = ({ children }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const isGamePage = location.pathname === '/game';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/85 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/70">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3 brand-lockup hover:opacity-80 transition-opacity">
            <Music className="h-6 w-6 text-[hsl(var(--accent))]" />
            <h1 className="font-[var(--font-display)] text-2xl tracking-tight">
              Nick's Name That Tune
            </h1>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Music Trivia
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {/* Play Game Button - Desktop */}
            <Link to="/game" className="hidden md:block">
              <Button 
                variant={isGamePage ? "default" : "outline"}
                className="rounded-xl"
                data-testid="play-game-button"
              >
                <Gamepad2 className="mr-2 h-4 w-4" />
                Play Game
              </Button>
            </Link>

            {/* Mobile nav trigger */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button
                  variant="outline"
                  size="icon"
                  data-testid="mobile-nav-trigger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <div className="p-4">
                  <h2 className="font-[var(--font-display)] text-xl mb-4">
                    Navigation
                  </h2>
                  <Link to="/game" onClick={() => setMobileNavOpen(false)}>
                    <Button className="w-full mb-4" data-testid="mobile-play-game-button">
                      <Gamepad2 className="mr-2 h-4 w-4" />
                      Play Game
                    </Button>
                  </Link>
                  <Link to="/" onClick={() => setMobileNavOpen(false)}>
                    <Button variant="outline" className="w-full mb-4">
                      <Music className="mr-2 h-4 w-4" />
                      View Rules
                    </Button>
                  </Link>
                  {!isGamePage && <RulesSidebarNav onItemClick={() => setMobileNavOpen(false)} />}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6 lg:gap-8">
          {/* Desktop sidebar - only on rules page */}
          {!isGamePage && (
            <aside className="hidden lg:block lg:sticky lg:top-24 h-[calc(100vh-8rem)] w-[280px] flex-shrink-0">
              <div className="rounded-[var(--radius)] border bg-white/70 backdrop-blur px-3 py-4">
                <h2 className="font-[var(--font-display)] text-lg mb-3 px-3">
                  Sections
                </h2>
                <RulesSidebarNav />
              </div>
            </aside>
          )}

          {/* Main content */}
          <main className={`flex-1 ${!isGamePage ? 'max-w-[920px]' : 'max-w-none'}`}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
